import { apiCall } from '../../api/ServerApi';
import { API_URL_PATHS } from '../../consts/ApiUrlPaths';
import { MAX_PERCENT_OF_ISSUES_TO_COVER } from '../../helpers/ChartsHelper';
import { addCollectionsToUrlParams, encodeCollectionIds } from '../../helpers/CollectionsHelper';
import { generateUrlWithViewId } from '../../helpers/PropertyViewsHelper';
import { IProperty } from '../../interfaces/Property';
import { IScan } from '../../interfaces/Scan';
import { DEFAULT_SORT_OPTION_VALUES } from '../../pages/dashboard/DashboardConsts';
import {
	COMPARE_TYPE,
	TActiveProperties,
	TActivePropertyDataParams,
	TAverageScoreKpi,
	TPagesMonitoredKpi,
	TQuickWins,
	TScansParams,
	TScansWithStatus,
	TTenantCriticalIssuesKpi
} from '../../pages/dashboard/DashboardTypes';
import { logger } from '../Logger';

const { TENANT_OVERVIEW_URL_PATH, PROPERTIES_URL_PATH } = API_URL_PATHS;

export const getTenantScoreKpi = async (
	compareType: COMPARE_TYPE,
	collectionIds: string[],
	showOnlyPropertiesInMyCollection: boolean,
	isMultipleCollectionsEnabled = false
): Promise<TAverageScoreKpi> => {
	const url = addCollectionsToUrlParams(
		`${TENANT_OVERVIEW_URL_PATH}/score?compareType=${compareType.toString()}`,
		collectionIds,
		showOnlyPropertiesInMyCollection,
		false,
		isMultipleCollectionsEnabled
	);
	let tenantScansScore;

	try {
		tenantScansScore = await apiCall('GET', url);
	} catch (err) {
		logger.error('error for tenant score kpi request: ', err);
	}
	return tenantScansScore;
};

export const getCriticalIssuesKpi = async (
	compareType: COMPARE_TYPE,
	collectionIds: string[],
	showOnlyPropertiesInMyCollection: boolean,
	isMultipleCollectionsEnabled = false
): Promise<TTenantCriticalIssuesKpi> => {
	const url = addCollectionsToUrlParams(
		`${TENANT_OVERVIEW_URL_PATH}/issues?compareType=${compareType.toString()}`,
		collectionIds,
		showOnlyPropertiesInMyCollection,
		false,
		isMultipleCollectionsEnabled
	);
	let tenantScansCriticalIssues;

	try {
		tenantScansCriticalIssues = await apiCall('GET', url);
	} catch (err) {
		logger.error('error for critical issues kpi request: ', err);
	}
	return tenantScansCriticalIssues;
};

export const getPagesMonitoredKpi = async (
	compareType: COMPARE_TYPE,
	collectionIds: string[],
	showOnlyPropertiesInMyCollection: boolean,
	isMultipleCollectionsEnabled = false
): Promise<TPagesMonitoredKpi> => {
	const url = addCollectionsToUrlParams(
		`${TENANT_OVERVIEW_URL_PATH}/pages?compareType=${compareType.toString()}`,
		collectionIds,
		showOnlyPropertiesInMyCollection,
		false,
		isMultipleCollectionsEnabled
	);
	let tenantScansPagesMonitored;

	try {
		tenantScansPagesMonitored = await apiCall('GET', url);
	} catch (err) {
		logger.error('error for pages monitored kpi request: ', err);
	}
	return tenantScansPagesMonitored;
};

export const getActivePropertiesList = async (
	collectionIds: string[],
	showOnlyPropertiesInMyCollection = false,
	isMultipleCollectionsEnabled = false,
	handleLoadingEnd?: () => void
): Promise<IProperty[]> => {
	const url = addCollectionsToUrlParams(
		`${PROPERTIES_URL_PATH}/active`,
		collectionIds,
		showOnlyPropertiesInMyCollection,
		true,
		isMultipleCollectionsEnabled
	);
	let activePropertiesList: IProperty[];

	try {
		activePropertiesList = await apiCall('GET', url);
	} catch (err) {
		logger.error('error for active properties request: ', err);
	} finally {
		handleLoadingEnd?.();
	}
	return activePropertiesList;
};

export const getActivePropertiesData = async ({
	views = false,
	sortField = DEFAULT_SORT_OPTION_VALUES.sortBy,
	sortDirection = DEFAULT_SORT_OPTION_VALUES.orderBy,
	pageNumber,
	pageSize,
	compareType,
	collectionIds,
	showOnlyPropertiesInMyCollection,
	isMultipleCollectionsEnabled = false
}: TActivePropertyDataParams): Promise<TActiveProperties> => {
	const requestParams = [
		`views=${views.toString()}`,
		`sortField=${sortField}`,
		`sortDirection=${sortDirection.toUpperCase()}`,
		`compareType=${compareType}`
	];
	if (pageNumber) {
		requestParams.push(`pageNumber=${pageNumber}`);
	}
	if (pageSize) {
		requestParams.push(`pageSize=${pageSize}`);
	}
	if (
		collectionIds.length > 0 &&
		(showOnlyPropertiesInMyCollection || isMultipleCollectionsEnabled)
	) {
		requestParams.push(`collectionIds=${encodeCollectionIds(collectionIds)}`);
	}

	let activePropertiesImpactData;
	try {
		activePropertiesImpactData = await apiCall(
			'GET',
			`property-overview?${requestParams.join('&')}`
		);
	} catch (err) {
		logger.error('error for property overview request: ', err);
	}
	return activePropertiesImpactData;
};

export const getActivePropertiesQuickWins = async ({
	topCriticalPropertiesCount = 8,
	topCriticalComponentsCount = 10,
	maxPercentCoverage = MAX_PERCENT_OF_ISSUES_TO_COVER,
	collectionIds,
	showOnlyPropertiesInMyCollection,
	isMultipleCollectionsEnabled = false
}: {
	collectionIds: string[];
	topCriticalPropertiesCount?: number;
	topCriticalComponentsCount?: number;
	maxPercentCoverage?: number;
	showOnlyPropertiesInMyCollection?: boolean;
	isMultipleCollectionsEnabled?: boolean;
}): Promise<TQuickWins> => {
	const url = addCollectionsToUrlParams(
		`${TENANT_OVERVIEW_URL_PATH}/components?topCriticalPropertiesCount=${topCriticalPropertiesCount}&topCriticalComponentsCount=${topCriticalComponentsCount}&maxPercentCoverage=${maxPercentCoverage}`,
		collectionIds,
		showOnlyPropertiesInMyCollection,
		false,
		isMultipleCollectionsEnabled
	);
	let quickWins: TQuickWins;

	try {
		quickWins = await apiCall('GET', url);
	} catch (err) {
		logger.error('error for quick wins request: ', err);
	}
	return quickWins;
};

export const getScans = async ({
	states = [],
	propertyId,
	viewId,
	collectionIds,
	showOnlyPropertiesInMyCollection,
	isMultipleCollectionsEnabled = false
}: TScansParams): Promise<IScan[]> => {
	const requestParams = [`states=${states.join(',')}`];
	if (propertyId) {
		requestParams.push(`propertyId=${propertyId}`);
	}
	if (
		collectionIds.length > 0 &&
		(showOnlyPropertiesInMyCollection || isMultipleCollectionsEnabled)
	) {
		requestParams.push(`collectionIds=${encodeCollectionIds(collectionIds)}`);
	}
	let scans: IScan[];
	try {
		scans = await apiCall('GET', generateUrlWithViewId(`scans?${requestParams.join('&')}`, viewId));
	} catch (err) {
		logger.error('error for scans request: ', err);
	}
	return scans;
};

export const getScansWithStatus = async ({
	states = [],
	propertyId,
	viewId,
	collectionIds,
	showOnlyPropertiesInMyCollection,
	isMultipleCollectionsEnabled = false
}: TScansParams): Promise<TScansWithStatus> => {
	const requestParams = [`states=${states.join(',')}`];
	if (propertyId) {
		requestParams.push(`propertyId=${propertyId}`);
	}
	if (
		collectionIds.length > 0 &&
		(showOnlyPropertiesInMyCollection || isMultipleCollectionsEnabled)
	) {
		requestParams.push(`collectionIds=${encodeCollectionIds(collectionIds)}`);
	}

	let scans: IScan[];
	try {
		scans = await apiCall('GET', generateUrlWithViewId(`scans?${requestParams.join('&')}`, viewId));
	} catch (err) {
		logger.error('error for scans request: ', err);
		return { failed: true, scans };
	}
	return { failed: false, scans };
};
